<template>
  <div ref="chartRoot" class="risk-chart-container">
    <div class="risk-chart-inner">
      <!-- Donut chart + overlays -->
      <div class="chart-donut-wrapper">
        <vue-apex-charts
          :key="chartKey"
          type="donut"
          height="260"
          :options="chartOptions"
          :series="series"
        />

        <!-- Dark USD overlay card (top-left) -->
        <div class="usd-card-overlay" :class="{ 'animate-card': animated }">
          <div class="usd-dot-row">
            <span class="color-dot" style="background: #eab308"></span>
            <span class="usd-label">USD</span>
          </div>
          <div class="usd-percentage">49%</div>
        </div>

        <!-- Bottom risk text -->
        <div class="bottom-risk-label" :class="{ 'animate-bottom': animated }">
          <span class="color-dot" style="background: #eab308"></span>
          <span class="risk-text"
            >1% Risk&nbsp;<span class="info-icon">ⓘ</span></span
          >
          <span class="risk-amount">£115m</span>
        </div>
      </div>

      <!-- White breakdown card (top-right) -->
      <div class="risk-info-card" :class="{ 'animate-card-right': animated }">
        <div class="total-risk-label">Total Risk</div>
        <div class="total-risk-amount">£945m</div>
        <div class="risk-breakdown">
          <div
            v-for="(item, index) in riskItems"
            :key="item.label"
            class="risk-item-row"
            :class="{ 'animate-row': animated }"
            :style="animated ? { animationDelay: (index * 80 + 300) + 'ms' } : {}"
          >
            <span class="color-dot" :style="{ background: item.color }"></span>
            <span class="item-label">{{ item.label }}</span>
            <span class="item-amount">{{ item.amount }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'RiskDonutChart',

  components: { VueApexCharts },

  data () {
    return {
      animated: false,
      chartKey: 0,
      series: [28, 267, 469, 180],

      riskItems: [
        { label: 'EUR', amount: '£28m', color: '#9333EA' },
        { label: 'Zinc', amount: '£267m', color: '#F97316' },
        { label: 'Copper', amount: '£469m', color: '#22C55E' },
        { label: 'USD', amount: '£180m', color: '#EAB308' }
      ],

      chartOptions: {
        chart: {
          type: 'donut',
          toolbar: { show: false },
          animations: {
            enabled: true,
            speed: 900,
            easing: 'easeinout',
            animateGradually: { enabled: true, delay: 120 },
            dynamicAnimation: { enabled: true, speed: 400 }
          }
        },
        colors: ['#9333EA', '#F97316', '#22C55E', '#EAB308'],
        labels: ['EUR', 'Zinc', 'Copper', 'USD'],
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: {
          width: 3,
          colors: ['#ffffff']
        },
        plotOptions: {
          pie: {
            donut: {
              size: '62%',
              labels: { show: false }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `£${val}m`
          }
        },
        states: {
          hover: {
            filter: { type: 'lighten', value: 0.05 }
          }
        }
      }
    }
  },

  mounted () {
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animated = false
            this.$nextTick(() => {
              this.chartKey += 1
              this.animated = true
            })
          } else {
            this.animated = false
          }
        })
      },
      { threshold: 0.3 }
    )
    this._observer.observe(this.$refs.chartRoot)
  },

  beforeDestroy () {
    if (this._observer) this._observer.disconnect()
  }
}
</script>

<style scoped>
.risk-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 8px;
}

.risk-chart-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

/* ── Donut area ─────────────────────────────── */
.chart-donut-wrapper {
  position: relative;
  width: 260px;
  flex-shrink: 0;
}

/* ── USD card overlay ───────────────────────── */
.usd-card-overlay {
  position: absolute;
  top: 14px;
  left: -10px;
  background: #1c2d45;
  color: #ffffff;
  border-radius: 12px;
  padding: 10px 16px;
  z-index: 10;
  min-width: 100px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.usd-dot-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.usd-label {
  font-size: 12px;
  font-weight: 500;
  color: #cbd5e1;
}

.usd-percentage {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 4px;
  color: #ffffff;
}

/* ── Bottom risk label ──────────────────────── */
.bottom-risk-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
  margin-top: 6px;
  padding-left: 4px;
}

.risk-text {
  color: #555;
}

.info-icon {
  font-size: 11px;
  color: #888;
}

.risk-amount {
  font-weight: 600;
  color: #222;
  margin-left: 4px;
}

/* ── Shared dot ─────────────────────────────── */
.color-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── White breakdown card ───────────────────── */
.risk-info-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  min-width: 170px;
  align-self: flex-start;
  margin-top: 10px;
}

.total-risk-label {
  font-size: 11px;
  color: #888;
  margin-bottom: 2px;
  font-weight: 500;
}

.total-risk-amount {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.risk-breakdown {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.risk-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #444;
}

.item-label {
  flex: 1;
  color: #555;
}

.item-amount {
  font-weight: 600;
  color: #0f172a;
}

/* ── Keyframe animations ────────────────────── */
@keyframes fadeSlideLeft {
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0); }
}

@keyframes fadeSlideRight {
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
}

@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.animate-card {
  animation: fadeSlideLeft 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.animate-card-right {
  animation: fadeSlideRight 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
}

.animate-bottom {
  animation: fadeSlideUp 0.45s ease 0.5s both;
}

.animate-row {
  opacity: 0;
  animation: fadeIn 0.4s ease forwards;
}
</style>
